/*
 * CAPTURE-OWNED MODULE
 * ReadOnlyZoneLayer — static, display-only rendering of template zones for the
 * INACTIVE pages of the continuous-scroll TemplateZoneViewer.
 *
 * Reuses ZoneEditorOverlay's box + label-pill markup and color scheme, minus
 * ALL interaction: no draw, select, move, resize, corner handles, keyboard, or
 * draw preview. The whole layer is pointer-events:none so clicks fall through
 * to the page wrapper (which focuses the page) and to the OCR layer beneath.
 *
 * Coordinate math is intentionally identical to ZoneEditorOverlay: a zone's
 * normalized 0..1 rect maps straight to a percentage box within the inset:0
 * page surface. Do NOT change it — the active (ZoneEditorOverlay) and inactive
 * (this) layers must position zones the same way so nothing shifts when a page
 * flips between interactive and read-only.
 *
 * Colors, labels, dimming, paint order and outline-only table/region zones
 * come from utils/zoneLabels, shared with ZoneEditorOverlay.
 */

import React from 'react';
import { ZONE_STYLES, zoneLabel, isZoneDimmed, isZoneLabelShown, isOutlineZone, zonePaintOrder, DIMMED_OPACITY } from '../utils/zoneLabels';

export default function ReadOnlyZoneLayer({ zones = [], fields = [], dimAnchorRegion = false, selectedFieldId = null, pickMode = false, pickFieldId = null }) {
  if (!zones.length) return null;

  return (
    <div
      className="zone-editor-overlay zone-editor-overlay--readonly"
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 15 }}
    >
      {zonePaintOrder(zones).map((zone) => {
        const style = ZONE_STYLES[zone.zone_type] || ZONE_STYLES.field;
        const label = zoneLabel(zone, fields);
        const dimmed = isZoneDimmed(zone, { dimAnchorRegion, selectedFieldId, pickMode, pickFieldId });
        const showLabel = isZoneLabelShown(zone, { dimAnchorRegion, selectedFieldId });

        return (
          <div
            key={zone.id}
            className="zone-box zone-box--readonly"
            data-zone-type={zone.zone_type}
            data-dimmed={dimmed ? 'true' : undefined}
            style={{
              position: 'absolute',
              left: `${zone.x * 100}%`,
              top: `${zone.y * 100}%`,
              width: `${zone.w * 100}%`,
              height: `${zone.h * 100}%`,
              background: isOutlineZone(zone) ? 'transparent' : style.bg,
              border: `1px dashed ${style.border}`,
              borderRadius: '2px',
              pointerEvents: 'none',
              opacity: dimmed ? DIMMED_OPACITY : 1,
            }}
          >
            {/* Zone label — compact pill above zone (matches ZoneEditorOverlay) */}
            {showLabel && (
              <div className="zone-label" style={{
                position: 'absolute',
                top: '-16px',
                left: '0',
                fontSize: '9px',
                fontWeight: 600,
                color: style.border,
                background: 'rgba(255,255,255,0.92)',
                padding: '1px 5px',
                borderRadius: '3px',
                border: `1px solid ${style.border}44`,
                lineHeight: '13px',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                maxWidth: '180px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {label}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
